import React from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { adminColors } from '../../theme/adminTheme';

// Shared by StudentDrawer.js and StaffDrawer.js — both fetch the same
// GET /api/users/{id}/groups shape ({id, name, school, time_slots,
// teachers}) and derive the same day-grouped view of it (groupScheduleByDay
// below); only the section's copy differs (a student's meeting times are
// also their sign-in window, a teacher's aren't).

export const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// "09:00" (24h, no seconds — see GroupTimeSlot.start_time in app/models.py)
// -> "9:00 AM". Falls back to the raw string on anything unexpected rather
// than throwing or silently blanking a real (if oddly formatted) value.
export function formatSlotTime(hhmm) {
  if (!hhmm) return '';
  const [hStr, mStr] = String(hhmm).split(':');
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = ((h + 11) % 12) + 1;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

// Flattens { groups: [{time_slots, ...}] } into a Monday-first list of
// { day, dayName, slots } — each slot carrying its own class name/time/
// teachers so a day with classes from multiple groups renders correctly.
export function groupScheduleByDay(scheduleGroups) {
  const map = new Map();
  (scheduleGroups || []).forEach((g) => {
    (g.time_slots || []).forEach((slot) => {
      const day = slot.day_of_week;
      if (day < 0 || day > 6) return;
      const list = map.get(day) || [];
      list.push({ className: g.name, start: slot.start_time, end: slot.end_time, teachers: g.teachers || [] });
      map.set(day, list);
    });
  });
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([day, slots]) => ({
      day,
      dayName: DAY_NAMES[day],
      slots: [...slots].sort((a, b) => (a.start || '').localeCompare(b.start || '')),
    }));
}

// Renders the loading/error/empty/populated states for a "Weekly Schedule"
// section — the heading itself (icon + "Weekly Schedule" title) stays in
// each caller since StudentDrawer also renders "Classes"/add-to-class
// controls right below it that StaffDrawer doesn't have.
//
// variant='student': these times gate sign-in (StudentDrawer's exact
// existing copy). variant='teacher': informational only — a teacher's own
// sign-in isn't gated by their teaching schedule, and their per-slot line
// omits the redundant "· <own name>" a student's line shows for its
// teacher(s).
export default function WeeklySchedule({ ownerName, scheduleByDay, loading, error, variant = 'student' }) {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }
  if (scheduleByDay.length === 0) {
    return (
      <Alert severity={variant === 'student' ? 'warning' : 'info'} variant="outlined" sx={{ mb: 3 }}>
        {variant === 'student'
          ? `No scheduled class times yet — ${ownerName} won't be able to sign in until they're added to a class with a schedule.`
          : `${ownerName} isn't assigned to teach any classes yet.`}
      </Alert>
    );
  }
  return (
    <>
      <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
        {variant === 'student'
          ? `These meeting times are also ${ownerName}'s sign-in window — the platform only lets a student in while one of their classes is in session.`
          : `${ownerName}'s teaching schedule, across every class they're assigned to.`}
      </Alert>
      <Box sx={{ mb: 3 }}>
        {scheduleByDay.map(({ day, dayName, slots }) => (
          <Box key={day} sx={{ mb: 1.5, '&:last-of-type': { mb: 0 } }}>
            <Typography
              variant="overline"
              sx={{ color: adminColors.accent, fontWeight: 700, letterSpacing: '0.06em', lineHeight: 1.8 }}
            >
              {dayName}
            </Typography>
            {slots.map((slot, idx) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key -- a class can repeat on the same day only with identical (start,end), no stable id to key by beyond position
                key={idx}
                sx={{ display: 'flex', alignItems: 'baseline', gap: 1, pl: 0.25 }}
              >
                <Typography variant="body2" sx={{ color: adminColors.ink, fontWeight: 600, minWidth: 132 }}>
                  {formatSlotTime(slot.start)}–{formatSlotTime(slot.end)}
                </Typography>
                <Typography variant="body2" sx={{ color: adminColors.inkSoft }} noWrap>
                  {slot.className}
                  {variant === 'student' && slot.teachers.length > 0
                    ? ` · ${slot.teachers.map((t) => t.username).join(', ')}`
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </>
  );
}
